import React from 'react';
import { Box, TableRow, TableCell, Tooltip, Typography } from '@mui/material';
import {
  BriefcaseBusiness,
  MapPin,
  ContactRound,
  CircleCheck,
  CircleMinus,
  BadgeCheck,
  Minus,
  Eye,
  Pencil,
  Trash2,
} from 'lucide-react';
import { ClienteListItem } from '../types';

interface Props {
  cliente: ClienteListItem;
  onEditar: (c: ClienteListItem) => void;
  onEliminar: (c: ClienteListItem) => void;
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w[0]?.toUpperCase() ?? '').join('');
}

export function ClienteRowCard({ cliente, onEditar, onEliminar }: Props) {
  const accentColor = cliente.activo ? '#7BA66F' : '#A9A4AA';

  return (
    <TableRow
      data-testid="cliente-row"
      sx={{
        height: 76,
        background: '#FFFFFF',
        border: '1px solid rgba(116, 48, 95, 0.10)',
        borderRadius: '8px',
        boxShadow: '0 3px 7px rgba(45,30,46,0.08), 0 12px 24px rgba(45,30,46,0.045)',
        transition: 'transform 170ms ease, box-shadow 170ms ease, border-color 170ms ease',
        transform: 'translateZ(0)',
        cursor: 'pointer',
        '& td': { borderBottom: 'none' },
        '& td:first-of-type': { borderRadius: '8px 0 0 8px' },
        '& td:last-of-type': { borderRadius: '0 8px 8px 0' },
        '&:hover': {
          transform: 'translateY(-3px) translateZ(0)',
          boxShadow: '0 8px 16px rgba(52,32,54,0.11), 0 18px 32px rgba(52,32,54,0.08)',
          borderColor: 'rgba(168,79,141,0.28)',
          background: '#FFFCFE',
        },
      }}
      onClick={() => onEditar(cliente)}
    >
      {/* Celda Cliente */}
      <TableCell sx={{ pl: 0, pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Acento lateral */}
          <Box
            sx={{
              width: 4,
              height: 52,
              borderRadius: '8px 0 0 8px',
              background: accentColor,
              flexShrink: 0,
            }}
          />
          {/* Avatar */}
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '8px',
              background: '#F1E3ED',
              border: '1px solid #E1C9D9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 750, color: '#74305F', lineHeight: 1 }}>
              {getInitials(cliente.razonSocial)}
            </Typography>
          </Box>
          {/* Nombre + NIT */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: '#2C232D',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 200,
              }}
            >
              {cliente.razonSocial}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#7A727C' }}>
              NIT {cliente.nit ?? '—'}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {/* Celda Actividad */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              background: '#FAF1F7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <BriefcaseBusiness size={20} color="#8A5077" />
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: '#514852',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              maxWidth: 160,
            }}
          >
            {cliente.actividadEconomica ?? 'Sin definir'}
          </Typography>
        </Box>
      </TableCell>

      {/* Celda Ubicación */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              background: '#EDF6F7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MapPin size={20} color="#4C7B87" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 650, color: '#3E353F', lineHeight: 1.2 }}>
              {cliente.ciudad}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 400, color: '#817984', lineHeight: 1.2 }}>
              {cliente.departamento}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {/* Celda Perfil tributario */}
      <TableCell>
        {cliente.granContribuyente ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: '9px',
              py: '6px',
              borderRadius: '999px',
              background: '#FFF6DF',
              color: '#6E4A16',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            <BadgeCheck size={17} />
            Gran contribuyente
          </Box>
        ) : (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: '9px',
              py: '6px',
              borderRadius: '999px',
              background: '#F2F0F2',
              color: '#827A84',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            <Minus size={17} />
            No aplica
          </Box>
        )}
      </TableCell>

      {/* Celda Contactos */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '8px',
              background: '#F3EDF7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ContactRound size={21} color="#6B4A82" />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#433A45' }}>
            {cliente.cantidadContactos}
          </Typography>
        </Box>
      </TableCell>

      {/* Celda Estado */}
      <TableCell>
        {cliente.activo ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              height: 30,
              px: '10px',
              borderRadius: '999px',
              background: '#EDF7EE',
              color: '#397A43',
              border: '1px solid #A9D0AE',
              fontSize: 12,
              fontWeight: 650,
            }}
          >
            <CircleCheck size={15} />
            Activo
          </Box>
        ) : (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              height: 30,
              px: '10px',
              borderRadius: '999px',
              background: '#F2F0F2',
              color: '#6F6871',
              border: '1px solid #D1CCD2',
              fontSize: 12,
              fontWeight: 650,
            }}
          >
            <CircleMinus size={15} />
            Inactivo
          </Box>
        )}
      </TableCell>

      {/* Celda Acciones */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Tooltip title="Ver cliente">
            <Box
              component="button"
              aria-label="Ver cliente"
              onClick={() => onEditar(cliente)}
              sx={{
                width: 38,
                height: 38,
                borderRadius: '8px',
                border: 'none',
                background: '#EFF4F6',
                color: '#4C6575',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(45,30,46,0.07)',
                transition: 'background 150ms, transform 150ms',
                '&:hover': { background: '#DDE9ED', transform: 'translateY(-1px)' },
              }}
            >
              <Eye size={18} />
            </Box>
          </Tooltip>

          <Tooltip title="Editar cliente">
            <Box
              component="button"
              aria-label="Editar cliente"
              data-testid="editar-btn"
              onClick={() => onEditar(cliente)}
              sx={{
                width: 38,
                height: 38,
                borderRadius: '8px',
                border: 'none',
                background: '#F8EEF5',
                color: '#87446F',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(45,30,46,0.07)',
                transition: 'background 150ms, transform 150ms',
                '&:hover': { background: '#EEDAE8', transform: 'translateY(-1px)' },
              }}
            >
              <Pencil size={18} />
            </Box>
          </Tooltip>

          <Tooltip title="Eliminar cliente">
            <Box
              component="button"
              aria-label="Eliminar cliente"
              data-testid="eliminar-btn"
              onClick={() => onEliminar(cliente)}
              sx={{
                width: 38,
                height: 38,
                borderRadius: '8px',
                border: 'none',
                background: '#FCEEEF',
                color: '#B4232C',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(45,30,46,0.07)',
                transition: 'background 150ms, transform 150ms',
                '&:hover': { background: '#F7DADC', transform: 'translateY(-1px)' },
              }}
            >
              <Trash2 size={18} />
            </Box>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}
